import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Box, Button, Checkbox, FormControlLabel, Popover, Stack } from '@mui/material';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import {
  computePercentChange,
  DashboardBlock,
  DashboardGroupHeading,
  DashboardPeriod,
  getPeriodRange,
  PeriodSelect,
  StatTile,
} from '@retail/dashboard/components';
import { useFetchUpdatedCompetitorPrices } from '@retail/dashboard/data-access';
import { Intervals, IntervalType } from '@retail/dashboard/types';
import { useDashboardTFunction } from '@retail/dashboard/i18n';
import { useFetchCompetitors, useFetchRetailers } from '@retail/retailer/data-access';
import { Retailer } from '@retail/retailer/types';
import { CompetitorPricesChart, CompetitorSeries } from './CompetitorPricesChart';
import { MarketIcon } from '@shared/custom-icons';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '@retail/products/utils';

const periodToInterval = (period: DashboardPeriod): IntervalType =>
  period === 'week' ? Intervals.WEEK : Intervals.MONTH;

interface Props {
  period: DashboardPeriod;
  onPeriodChange: (period: DashboardPeriod) => void;
  periods: readonly DashboardPeriod[];
}

export const CompetitorPricesBlock = ({ period, onPeriodChange, periods }: Props) => {
  const t = useDashboardTFunction();
  const { i18n } = useTranslation();
  const { selectedOrgUnitAssortment } = useSelectedOrgUnit();
  const assortmentId = selectedOrgUnitAssortment.assortmentId;

  const [selected, setSelected] = useState<number[] | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const range = useMemo(() => getPeriodRange(period), [period]);
  const interval = periodToInterval(period);
  const fmt = (d: dayjs.Dayjs) => d.format('YYYY-MM-DD');

  const prices = useFetchUpdatedCompetitorPrices({
    assortmentId,
    from: fmt(range.previousFrom),
    to: fmt(range.to),
    onlyLatest: true,
    interval,
    suspense: false,
    refetchOnWindowFocus: false,
  });

  const { data: competitorDtos = [] } = useFetchCompetitors(selectedOrgUnitAssortment.orgUnitId);
  const { data: retailerDtos = [] } = useFetchRetailers();
  const retailerName = useMemo(() => {
    const retailers = retailerDtos
      .filter(({ name, urlName, stores }) => name && urlName && stores?.length)
      .map((dto) => Retailer.fromDto(dto, competitorDtos, ''));
    const byId = new Map(retailers.map((r) => [r.id, r.name]));
    return (id: number) => byId.get(id) ?? String(id);
  }, [competitorDtos, retailerDtos]);

  const boundary = fmt(range.from);
  const currentData = useMemo(
    () => (prices.data ?? []).filter((p) => p.fromDate >= boundary),
    [prices.data, boundary]
  );
  const previousData = useMemo(
    () => (prices.data ?? []).filter((p) => p.fromDate < boundary),
    [prices.data, boundary]
  );

  const buckets = useMemo(
    () => Array.from(new Set(currentData.map((p) => p.fromDate))).sort(),
    [currentData]
  );
  const bucketLabels = useMemo(() => buckets.map((b) => dayjs(b).format('DD.MM')), [buckets]);

  const allIds = useMemo(
    () => Array.from(new Set(currentData.map((p) => p.retailerId))),
    [currentData]
  );
  const selectedIds = selected ?? allIds;

  const toggle = (id: number) =>
    setSelected((prev) => {
      const base = prev ?? allIds;
      return base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
    });

  const series: CompetitorSeries[] = useMemo(
    () =>
      allIds
        .filter((id) => selectedIds.includes(id))
        .map((id) => ({ id, name: retailerName(id), colorIndex: allIds.indexOf(id) })),
    [allIds, selectedIds, retailerName]
  );

  const rows = useMemo(() => {
    const byBucket = new Map<string, Record<string, number | string>>(
      buckets.map((b) => [b, { bucket: b }])
    );
    currentData.forEach((p) => {
      if (!selectedIds.includes(p.retailerId)) return;
      const row = byBucket.get(p.fromDate);
      if (row) row[String(p.retailerId)] = p.count;
    });
    return buckets.map((b) => byBucket.get(b) as Record<string, number | string>);
  }, [buckets, currentData, selectedIds]);

  const currentTotal = useMemo(
    () =>
      currentData
        .filter((p) => selectedIds.includes(p.retailerId))
        .reduce((sum, p) => sum + p.count, 0),
    [currentData, selectedIds]
  );
  const previousTotal = useMemo(
    () =>
      previousData
        .filter((p) => selectedIds.includes(p.retailerId))
        .reduce((sum, p) => sum + p.count, 0),
    [previousData, selectedIds]
  );

  const isEmpty = !prices.isLoading && !prices.isError && buckets.length === 0;

  return (
    <Stack gap={1.5} sx={{ height: '100%' }}>
      <DashboardGroupHeading
        title={t('dashboard.updatedCompetitorPrices')}
        icon={<MarketIcon fontSize="small" />}
        action={
          <Stack direction="row" gap={1} alignItems="center">
            <PeriodSelect value={period} onChange={onPeriodChange} periods={periods} />
          </Stack>
        }
      />
      <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex' }}>
        <DashboardBlock
          isLoading={prices.isLoading}
          isError={prices.isError}
          onRetry={prices.refetch}
          isEmpty={isEmpty}
          minHeight={300}
        >
          <Stack gap={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="start" gap={2}>
              <StatTile
                label={t('dashboard.competitorPrices.total')}
                value={formatPrice(currentTotal, i18n.language)}
                change={
                  range.isFuture ? undefined : computePercentChange(currentTotal, previousTotal)
                }
                caption={t('dashboard.period.vsPrevious')}
              />
              <Button
                size="small"
                color="secondary"
                variant="outlined"
                startIcon={<TuneOutlinedIcon fontSize="small" />}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{ whiteSpace: 'nowrap', textTransform: 'none' }}
              >
                {t('dashboard.competitorPrices.selectCompetitors')} ({selectedIds.length}/
                {allIds.length})
              </Button>
              <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Stack sx={{ p: 1.5, maxHeight: 320, overflowY: 'auto' }}>
                  {allIds.map((id) => (
                    <FormControlLabel
                      key={id}
                      control={
                        <Checkbox
                          size="small"
                          checked={selectedIds.includes(id)}
                          onChange={() => toggle(id)}
                        />
                      }
                      label={retailerName(id)}
                    />
                  ))}
                </Stack>
              </Popover>
            </Stack>

            <Box>
              <CompetitorPricesChart
                buckets={buckets}
                bucketLabels={bucketLabels}
                series={series}
                rows={rows}
              />
            </Box>
          </Stack>
        </DashboardBlock>
      </Box>
    </Stack>
  );
};
